import { useCallback, useLayoutEffect, useMemo, useRef, useState, type Dispatch, type SetStateAction } from 'react';
import { useSI } from '../../contexts/SIContext';

export function useSIPanelState<T>(key: string, initialValue: T): [T, Dispatch<SetStateAction<T>>] {
 const { readCached, writeCached } = useSI();
 const restored = useMemo(() => readCached<T>(key) ?? initialValue, [key, readCached]);
 const [stored, setStored] = useState({ key, value: restored });
 useLayoutEffect(() => {
  if (stored.key !== key) setStored({ key, value: restored });
 }, [key, restored, stored.key]);
 const value = stored.key === key ? stored.value : restored;
 const latest = useRef({ key, value }); latest.current = { key, value };
 const setValue = useCallback<Dispatch<SetStateAction<T>>>(update => {
  const current = latest.current.key === key ? latest.current.value : readCached<T>(key) ?? initialValue;
  const next = typeof update === 'function' ? (update as (value: T) => T)(current) : update;
  writeCached(key, next); latest.current = { key, value: next }; setStored({ key, value: next });
 }, [key, readCached, writeCached]);
 return [value, setValue];
}
